import { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Map as MapIcon } from "lucide-react";
import { Project } from "@/types";
import { TooltipProvider } from "@/components/ui/tooltip";
import ProjectPin from "./ProjectPin";
import { createRoot } from "react-dom/client";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";

interface DashboardMapProps {
  coordinates: [number, number]; // [longitude, latitude] - center coordinates
  projects?: Project[]; // Array of projects to display on the map
  projectTitle?: string; // Optional title for single project view
  className?: string;
  onSelectProject?: (project: Project) => void;
  selectedProject?: Project | null;
  customerColor?: string;
  t: (key: string) => string;
}

const DashboardMap = ({
  coordinates,
  projects = [],
  className,
  onSelectProject,
  selectedProject,
  customerColor = "#00383c",
  t,
}: DashboardMapProps) => {
  const { language } = useLanguage();

  const mapboxToken =
    "pk.eyJ1IjoidG9ic3V0IiwiYSI6ImNtODdmb3ByNTBmbzIybHM4MG1xZ2dqY2gifQ.Ta5Zr0uvEsNaz2lAH63f1w";
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<{ [id: string]: mapboxgl.Marker }>({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isManualZoom, setIsManualZoom] = useState(false); // Track manual zooming

  const initializeMap = useCallback(() => {
    if (!mapContainer.current || map.current) return;

    mapboxgl.accessToken = mapboxToken;

    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: coordinates,
      zoom: 9, // Default zoom level
      minZoom: 1.5,
      projection: "globe",
      attributionControl: false,
    });

    newMap.addControl(
      new mapboxgl.NavigationControl({
        visualizePitch: true,
        showCompass: true,
        showZoom: true,
      }),
      "top-right"
    );

    newMap.addControl(new mapboxgl.AttributionControl(), "bottom-right");
    newMap.addControl(new mapboxgl.FullscreenControl(), "top-right");

    newMap.on("style.load", () => {
      newMap.setFog({
        range: [0.8, 8],
        color: "rgb(220, 230, 240)",
        "horizon-blend": 0.1,
      });

      newMap.addSource("mapbox-dem", {
        type: "raster-dem",
        url: "mapbox://mapbox.mapbox-terrain-dem-v1",
      });

      newMap.setTerrain({
        source: "mapbox-dem",
        exaggeration: 1.5,
      });

      // Automatically fit bounds to include all projects
      if (projects.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        projects.forEach((project) => {
          if (project.geom && project.geom.type === "Point") {
            const pointGeom = project.geom as GeoJSON.Point;
            const coords = pointGeom.coordinates;
            if (coords && coords.length >= 2) {
              bounds.extend([coords[0], coords[1]]);
            }
          }
        });

        if (!bounds.isEmpty()) {
          newMap.fitBounds(bounds, {
            padding: 100,
            maxZoom: 14,
          });
        }
      }

      setMapLoaded(true);
    });

    newMap.on("movestart", () => {
      setIsManualZoom(true); // Set manual zoom flag when the user interacts with the map
    });

    map.current = newMap;

    return () => {
      Object.values(markersRef.current).forEach((marker) => marker.remove());
      newMap.remove();
      map.current = null;
    };
  }, [coordinates, mapboxToken, projects]);

  useEffect(() => {
    initializeMap();
  }, [initializeMap]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !projects || projects.length === 0)
      return;

    Object.values(markersRef.current).forEach((marker) => marker.remove());
    markersRef.current = {};

    projects.forEach((project) => {
      if (!project.geom || project.geom.type !== "Point") return;

      const pointGeom = project.geom as GeoJSON.Point;
      const coords = pointGeom.coordinates;

      if (!coords || coords.length < 2) return;

      const markerElement = document.createElement("div");
      const root = createRoot(markerElement);

      root.render(
        <TooltipProvider>
          <ProjectPin
            project={project}
            shouldPulse={selectedProject === null}
            isSelected={selectedProject?.id === project.id}
            onClick={() => onSelectProject && onSelectProject(project)}
            customColor={customerColor}
            t={t}
            language={language}
          />
        </TooltipProvider>
      );

      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat([coords[0], coords[1]])
        .addTo(map.current!);

      markersRef.current[project.id] = marker; // Store the marker reference
    });

    if (projects.length > 1 && !isManualZoom) {
      const bounds = new mapboxgl.LngLatBounds();

      projects.forEach((project) => {
        if (!project.geom || project.geom.type !== "Point") return;

        const pointGeom = project.geom as GeoJSON.Point;
        const coords = pointGeom.coordinates;

        if (!coords || coords.length < 2) return;

        bounds.extend([coords[0], coords[1]]);
      });

      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 100,
          maxZoom: 14,
        });
      }
    }
  }, [
    mapLoaded,
    projects,
    selectedProject,
    onSelectProject,
    isManualZoom,
    customerColor,
    t,
    language,
  ]);

  useEffect(() => {
    if (
      !map.current ||
      !selectedProject ||
      !selectedProject.geom ||
      selectedProject.geom.type !== "Point"
    )
      return;

    const pointGeom = selectedProject.geom as GeoJSON.Point;
    const coords = pointGeom.coordinates;

    if (!coords || coords.length < 2) return;

    setIsManualZoom(false); // Reset manual zoom flag before flying to the location

    map.current.flyTo({
      center: [coords[0], coords[1]],
      zoom: 12,
      pitch: 45,
      bearing: Math.random() * 30 - 15,
      duration: 2000,
      essential: true,
    });
  }, [selectedProject]);

  useEffect(() => {
    if (!map.current || selectedProject !== null) return;

    // Reset the map to the initial coordinates and zoom level
    const bounds = new mapboxgl.LngLatBounds();

    projects.forEach((project) => {
      if (project.geom && project.geom.type === "Point") {
        const pointGeom = project.geom as GeoJSON.Point;
        const coords = pointGeom.coordinates;
        if (coords && coords.length >= 2) {
          bounds.extend([coords[0], coords[1]]);
        }
      }
    });

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, {
        padding: 100,
        maxZoom: 14,
      });
    } else {
      map.current.flyTo({
        center: coordinates,
        zoom: 9, // Default zoom level
        pitch: 0,
        bearing: 0,
        duration: 1000, // Smooth transition
        essential: true,
      });
    }
  }, [selectedProject, coordinates, projects]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pin-pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.7);
        }
        70% {
          box-shadow: 0 0 0 10px rgba(52, 211, 153, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(52, 211, 153, 0);
        }
      }

      .pin-pulse {
        animation: pin-pulse 1.5s infinite;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!coordinates) {
    return (
      <div
        className={`bg-gray-100 rounded-xl flex items-center justify-center p-4 ${className}`}
      >
        <div className="text-center text-gray-500">
          <MapIcon className="h-5 w-5 mx-auto mb-2" />
          <p className="text-sm">Location data unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <div className={`w-full h-full ${className}`}>
        <div
          ref={mapContainer}
          className="w-full h-full rounded-xl touch-manipulation"
        />
      </div>
    </LanguageProvider>
  );
};

export default DashboardMap;
